import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { CloudDownload, CloudUpload, Download } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthToken } from '../../lib/authToken';

/**
 * Firm-level import/export (Wave X3/X4) — Settings tab panel.
 */
const DataPortabilityPanel: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.permissions?.['settings:write'];
  const canWriteCases = user?.role === 'admin' || user?.permissions?.['cases:write'];
  const canWriteClients = user?.role === 'admin' || user?.permissions?.['clients:write'];
  const canWriteDocs = user?.role === 'admin' || user?.permissions?.['documents:write'];

  const [piiAttestation, setPiiAttestation] = useState(false);
  const [busy, setBusy] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${getAuthToken()}`,
  });

  const downloadBlob = async (res: Response, fallbackName: string) => {
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || fallbackName;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const firmBackup = async () => {
    if (!piiAttestation) {
      toast.error('Confirm PII attestation first');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/case-packages/firm-backup', {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ piiAttestation: true }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Backup failed'));
      await downloadBlob(res, 'argislegal-firm-backup.zip');
      toast.success('Firm CSV backup downloaded — handle as confidential');
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Firm backup failed'));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async (kind: 'contacts' | 'matters') => {
    try {
      const res = await fetch(`/api/case-packages/templates/${kind}.csv`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Template download failed'));
      await downloadBlob(res, `argislegal-${kind}-template.csv`);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Template download failed'));
    }
  };

  const importCsv = async (kind: 'contacts' | 'matters', file: File, dryRun: boolean) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('dryRun', dryRun ? 'true' : 'false');
      const res = await fetch(`/api/case-packages/import/${kind}-csv`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Import failed'));
      const json = await res.json();
      toast.success(
        `${dryRun ? 'Dry-run' : 'Import'}: created ${json.created}, skipped ${json.skipped}, errors ${json.errors?.length || 0}`
      );
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const importPackage = async (file: File, dryRun: boolean) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('package', file);
      form.append('dryRun', dryRun ? 'true' : 'false');
      const res = await fetch('/api/case-packages/import', {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Package import failed'));
      const json = await res.json();
      if (json.dryRun) {
        toast.success(
          `Dry-run OK: ${json.preview?.sourceCaseNumber || 'matter'} · ${json.preview?.documentCount || 0} docs`
        );
      } else {
        toast.success(
          `Imported case ${json.data?.case?.caseNumber} with ${json.data?.importedDocuments || 0} documents`
        );
      }
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Package import failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Alert severity="warning">
        Data portability moves PII across system boundaries. Use dry-run first.
        Exports are audited. Privilege-withheld and confidential files stay excluded
        from case packages unless explicitly included on Case Folder export.
      </Alert>

      {(canWriteCases || canWriteDocs) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import ArgisLegal Case Package
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a <code>argislegal-case-*.zip</code> produced by Case Folder export.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={busy}
              >
                Dry-run package
                <input
                  type="file"
                  hidden
                  accept=".zip,application/zip"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void importPackage(f, true);
                    e.target.value = '';
                  }}
                />
              </Button>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
                disabled={busy || !(canWriteCases && canWriteDocs)}
              >
                Import package
                <input
                  type="file"
                  hidden
                  accept=".zip,application/zip"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void importPackage(f, false);
                    e.target.value = '';
                  }}
                />
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            CSV migration templates (Clio-compatible fields)
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {canWriteClients && (
              <Button
                size="small"
                startIcon={<Download />}
                onClick={() => downloadTemplate('contacts')}
              >
                Contacts template
              </Button>
            )}
            {canWriteCases && (
              <Button
                size="small"
                startIcon={<Download />}
                onClick={() => downloadTemplate('matters')}
              >
                Matters template
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canWriteClients && (
              <>
                <Button variant="outlined" component="label" disabled={busy}>
                  Dry-run contacts CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void importCsv('contacts', f, true);
                      e.target.value = '';
                    }}
                  />
                </Button>
                <Button variant="contained" component="label" disabled={busy}>
                  Import contacts CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void importCsv('contacts', f, false);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </>
            )}
            {canWriteCases && (
              <>
                <Button variant="outlined" component="label" disabled={busy}>
                  Dry-run matters CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void importCsv('matters', f, true);
                      e.target.value = '';
                    }}
                  />
                </Button>
                <Button variant="contained" component="label" disabled={busy}>
                  Import matters CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void importCsv('matters', f, false);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Firm CSV backup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Downloads contacts + matters CSVs in a ZIP (MyCase-style structured backup).
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={piiAttestation}
                  onChange={(e) => setPiiAttestation(e.target.checked)}
                  color="warning"
                />
              }
              label="I attest this firm backup is authorized and will be handled as confidential PII."
            />
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                startIcon={<CloudDownload />}
                onClick={firmBackup}
                disabled={busy || !piiAttestation}
              >
                Download firm backup
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default DataPortabilityPanel;
